import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, Info, ArrowRight } from 'lucide-react';
import type { ModeloCobertura, TrabajosConfig } from '@/api/installation.api';
import * as installationApi from '@/api/installation.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Toggle } from '@/components/ui/Toggle';
import { Modal } from '@/components/ui/Modal';
import { etiquetaCobertura } from '@/lib/cierre-validacion';

/**
 * Pestaña TRABAJOS de la configuración (tanda EMT-CNV-2026-01).
 *
 * Acá vive todo lo que el dueño controla del módulo Trabajos: el catálogo de
 * modelos con su período de garantía, las formas de pago del cierre y —para las
 * garantías— cómo se resolvió y por qué falló.
 *
 * El dueño NO es programador: nada de "id", "enum" ni "catálogo N1". Cada
 * tarjeta dice para qué sirve y dónde se ve el efecto.
 *
 * Son ocho tarjetas largas, así que arriba hay un índice que salta a cada una:
 * antes había que bajar a ciegas buscando la que se quería tocar.
 *
 * Los MODELOS ya no se editan acá: son los mismos del catálogo de venta y se
 * cargan en la pestaña Catálogo, con su precio. Lo que queda es el resumen, en
 * solo lectura, para poder mirar la cobertura sin cambiar de pestaña.
 */

/** Las tarjetas de la pestaña, para el índice y para el `id` de cada una. */
const SECCIONES: { id: string; nombre: string }[] = [
  { id: 'trabajos-modelos', nombre: 'Modelos y garantía' },
  { id: 'trabajos-pagos', nombre: 'Formas de pago' },
  { id: 'trabajos-resolucion', nombre: 'Cómo se resolvió la garantía' },
  { id: 'trabajos-causas', nombre: 'Por qué falló' },
  { id: 'trabajos-no-completado', nombre: 'Por qué no se completó' },
  { id: 'trabajos-urgencia', nombre: 'Urgencia de las garantías' },
  { id: 'trabajos-ubicacion', nombre: 'Ubicación de los trabajos' },
  { id: 'trabajos-resumen', nombre: 'Resumen del día' },
  { id: 'trabajos-encuesta', nombre: 'Encuesta de garantías' },
];

/** Índice pegajoso: salta a la tarjeta sin ensuciar la dirección con un #. */
function IndiceDeSecciones() {
  return (
    <nav
      aria-label="Secciones de Trabajos"
      className="sticky top-0 z-10 -mx-1 flex flex-wrap gap-1 rounded-lg border border-surface-border bg-white/95 p-2 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95"
    >
      {SECCIONES.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          onClick={(e) => {
            e.preventDefault();
            document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          {s.nombre}
        </a>
      ))}
    </nav>
  );
}

/** Id local para filas nuevas (el server solo exige que sea único y estable). */
const nuevoId = (prefijo: string) =>
  `${prefijo}-${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;

export function TrabajosTab({
  value,
  onChange,
  modelos,
  onEditarEnCatalogo,
}: {
  value: TrabajosConfig;
  onChange: (patch: Partial<TrabajosConfig>) => void;
  /** Los modelos con su cobertura, tal como quedarían al guardar el Catálogo. */
  modelos: ModeloCobertura[];
  /** Lleva a la pestaña Catálogo, que es donde se editan los modelos. */
  onEditarEnCatalogo: () => void;
}) {
  // Confirmación antes de borrar: un catálogo borrado de un clic deja trabajos
  // viejos apuntando a algo que ya no existe.
  const [porBorrar, setPorBorrar] = useState<{
    tipo: 'formaPago' | 'nivel' | 'causa' | 'motivo';
    id: string;
    nombre: string;
  } | null>(null);

  // PUNTO 5 · Consumo del mes: se lee aparte de la config porque es un dato
  // calculado, no algo que el dueño edite.
  const consumo = useQuery({
    queryKey: ['installation', 'consumo-mapas'],
    queryFn: installationApi.getConsumoMapas,
  });

  // PUNTO 6 · Volumen de saliente automático del mes: el dueño lo usa para
  // medir el efecto del resumen diario antes de tocar la encuesta.
  const saliente = useQuery({
    queryKey: ['installation', 'saliente-mes'],
    queryFn: installationApi.getSalienteDelMes,
  });

  const formasPago = value.formasPago ?? [];
  const niveles = value.nivelesResolucion ?? [];
  const causas = value.causasRaiz ?? [];
  const motivos = value.motivosNoCompletado ?? [];
  const pagosActivos = formasPago.filter((f) => f.activa).length;

  function confirmarBorrado() {
    if (!porBorrar) return;
    const { tipo, id } = porBorrar;
    if (tipo === 'formaPago') onChange({ formasPago: formasPago.filter((f) => f.id !== id) });
    if (tipo === 'nivel') onChange({ nivelesResolucion: niveles.filter((n) => n.id !== id) });
    if (tipo === 'causa') onChange({ causasRaiz: causas.filter((c) => c.id !== id) });
    if (tipo === 'motivo') onChange({ motivosNoCompletado: motivos.filter((m) => m.id !== id) });
    setPorBorrar(null);
  }

  return (
    <div className="space-y-4">
      <IndiceDeSecciones />

      {/* ---------------------------- MODELOS ----------------------------
          Resumen en SOLO LECTURA: el modelo y su garantía se cargan en la
          pestaña Catálogo, junto con el precio y las fotos. Tener dos listas
          de modelos fue justo lo que hizo que el panel dijera «no se puede
          saber si está en garantía». */}
      <Card id="trabajos-modelos">
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Modelos y su garantía</CardTitle>
              <CardDescription>
                Los modelos que el técnico puede elegir al cerrar un trabajo, y cuánto tiempo quedan
                cubiertos. Con esto el sistema calcula solo si una garantía entra o no en cobertura.
                Se cargan en el Catálogo, con su precio.
              </CardDescription>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={onEditarEnCatalogo}>
              Editar en Catálogo <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </CardHeader>
        <CardBody className="space-y-2">
          {modelos.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              No hay ningún modelo con garantía cargada. Mientras siga así, ninguna garantía se va a
              poder calcular: cargue los meses de cada modelo en el Catálogo.
            </p>
          )}
          {modelos.map((m) => (
            <div
              key={m.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 p-3 dark:border-slate-800"
            >
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {m.nombre}
              </span>
              <Badge variant="default">{etiquetaCobertura(m.mesesCobertura)}</Badge>
            </div>
          ))}
        </CardBody>
      </Card>

      {/* -------------------------- FORMAS DE PAGO ------------------------ */}
      <Card id="trabajos-pagos">
        <CardHeader>
          <CardTitle>Formas de pago</CardTitle>
          <CardDescription>
            Las opciones que ve supervisión al cerrar un trabajo en firme. Apagar una la saca de la
            lista sin borrar el historial de los trabajos que ya la usaron.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {pagosActivos === 0 && formasPago.length > 0 && (
            <p className="flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              No queda ninguna forma de pago encendida. Nadie va a poder cerrar un trabajo con venta
              hasta que encienda al menos una.
            </p>
          )}
          {formasPago.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              No hay formas de pago cargadas. Sin ellas no se puede cerrar un trabajo con venta.
            </p>
          )}

          {formasPago.map((f, i) => (
            <div
              key={f.id}
              className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 p-3"
            >
              <Input
                className="min-w-[10rem] flex-1"
                value={f.nombre}
                placeholder="Efectivo"
                onChange={(e) => {
                  const next = [...formasPago];
                  next[i] = { ...f, nombre: e.target.value };
                  onChange({ formasPago: next });
                }}
              />
              <div className="flex items-center gap-2">
                <Toggle
                  checked={f.activa}
                  label={`Usar la forma de pago ${f.nombre || 'sin nombre'}`}
                  onChange={(activa) => {
                    const next = [...formasPago];
                    next[i] = { ...f, activa };
                    onChange({ formasPago: next });
                  }}
                />
                <span className="text-xs text-slate-600">
                  {f.activa ? 'Se puede usar' : 'Apagada'}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Quitar la forma de pago ${f.nombre || 'sin nombre'}`}
                onClick={() => setPorBorrar({ tipo: 'formaPago', id: f.id, nombre: f.nombre })}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ))}

          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              onChange({
                formasPago: [...formasPago, { id: nuevoId('pago'), nombre: '', activa: true }],
              })
            }
          >
            <Plus className="mr-1 h-4 w-4" />
            Agregar forma de pago
          </Button>
        </CardBody>
      </Card>

      {/* --------------------- NIVELES DE RESOLUCIÓN --------------------- */}
      <Card id="trabajos-resolucion">
        <CardHeader>
          <CardTitle>Cómo se resolvió la garantía</CardTitle>
          <CardDescription>
            El técnico elige una de estas opciones al cerrar una garantía, y supervisión la confirma
            o la corrige. Sirve para saber cuántas garantías se resuelven por teléfono y cuántas
            necesitan que alguien vaya.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {niveles.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              No hay opciones cargadas. Sin ellas no se puede cerrar una garantía.
            </p>
          )}
          {niveles.map((n, i) => (
            <div
              key={n.id}
              className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-[6rem_1fr_auto_auto] sm:items-end"
            >
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Código interno
                </span>
                <Input
                  value={n.codigo}
                  placeholder="N1"
                  onChange={(e) => {
                    const next = [...niveles];
                    next[i] = { ...n, codigo: e.target.value };
                    onChange({ nivelesResolucion: next });
                  }}
                />
                <span className="mt-1 block text-[11px] text-slate-500">
                  El nombre corto con el que esta opción sale en los reportes. El cliente nunca lo
                  ve.
                </span>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-slate-600">
                  Cómo se ve en pantalla
                </span>
                <Input
                  value={n.nombre}
                  placeholder="Remota — sin visita"
                  onChange={(e) => {
                    const next = [...niveles];
                    next[i] = { ...n, nombre: e.target.value };
                    onChange({ nivelesResolucion: next });
                  }}
                />
              </label>
              <div className="flex items-center gap-2">
                <Toggle
                  checked={n.activo}
                  label={`Usar la opción ${n.nombre || 'sin nombre'}`}
                  onChange={(activo) => {
                    const next = [...niveles];
                    next[i] = { ...n, activo };
                    onChange({ nivelesResolucion: next });
                  }}
                />
                <span className="text-xs text-slate-600">{n.activo ? 'En uso' : 'Apagada'}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Quitar la opción ${n.nombre || 'sin nombre'}`}
                onClick={() => setPorBorrar({ tipo: 'nivel', id: n.id, nombre: n.nombre })}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              onChange({
                nivelesResolucion: [
                  ...niveles,
                  { id: nuevoId('niv'), codigo: '', nombre: '', activo: true },
                ],
              })
            }
          >
            <Plus className="mr-1 h-4 w-4" />
            Agregar opción
          </Button>
        </CardBody>
      </Card>

      {/* ------------------------- CAUSAS RAÍZ --------------------------- */}
      <Card id="trabajos-causas">
        <CardHeader>
          <CardTitle>Por qué falló</CardTitle>
          <CardDescription>
            La causa de la garantía. El técnico propone una y supervisión la confirma o la corrige;
            el reporte usa la de supervisión. Es lo que después dice si el problema viene de
            fábrica, de la instalación o del uso.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {causas.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              No hay causas cargadas. Sin ellas no se puede cerrar una garantía.
            </p>
          )}
          {causas.map((c, i) => (
            <div
              key={c.id}
              className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 p-3"
            >
              <Input
                className="min-w-[12rem] flex-1"
                value={c.nombre}
                placeholder="Desperfecto de fábrica"
                onChange={(e) => {
                  const next = [...causas];
                  next[i] = { ...c, nombre: e.target.value };
                  onChange({ causasRaiz: next });
                }}
              />
              <div className="flex items-center gap-2">
                <Toggle
                  checked={c.activo}
                  label={`Usar la causa ${c.nombre || 'sin nombre'}`}
                  onChange={(activo) => {
                    const next = [...causas];
                    next[i] = { ...c, activo };
                    onChange({ causasRaiz: next });
                  }}
                />
                <span className="text-xs text-slate-600">{c.activo ? 'En uso' : 'Apagada'}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Quitar la causa ${c.nombre || 'sin nombre'}`}
                onClick={() => setPorBorrar({ tipo: 'causa', id: c.id, nombre: c.nombre })}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              onChange({
                causasRaiz: [...causas, { id: nuevoId('causa'), nombre: '', activo: true }],
              })
            }
          >
            <Plus className="mr-1 h-4 w-4" />
            Agregar causa
          </Button>
        </CardBody>
      </Card>

      {/* ------------- MOTIVOS DE NO COMPLETADO (S-07) ------------------- */}
      <Card id="trabajos-no-completado">
        <CardHeader>
          <CardTitle>Por qué un trabajo no se completó</CardTitle>
          <CardDescription>
            El técnico elige uno de estos al marcar «No terminé». No puede escribirlo a mano: si
            cada uno lo escribe distinto, después no se puede contar cuántas veces pasó cada cosa.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-3">
          {motivos.length === 0 && (
            <p className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              No hay motivos cargados. Sin ellos el técnico no puede marcar «No terminé».
            </p>
          )}
          {motivos.map((m, i) => (
            <div
              key={m.id}
              className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 p-3"
            >
              <Input
                className="min-w-[12rem] flex-1"
                value={m.nombre}
                placeholder="Cliente ausente"
                onChange={(e) => {
                  const next = [...motivos];
                  next[i] = { ...m, nombre: e.target.value };
                  onChange({ motivosNoCompletado: next });
                }}
              />
              <div className="flex items-center gap-2">
                <Toggle
                  checked={m.activo}
                  label={`Usar el motivo ${m.nombre || 'sin nombre'}`}
                  onChange={(activo) => {
                    const next = [...motivos];
                    next[i] = { ...m, activo };
                    onChange({ motivosNoCompletado: next });
                  }}
                />
                <span className="text-xs text-slate-600">{m.activo ? 'En uso' : 'Apagado'}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                aria-label={`Quitar el motivo ${m.nombre || 'sin nombre'}`}
                onClick={() => setPorBorrar({ tipo: 'motivo', id: m.id, nombre: m.nombre })}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              onChange({
                motivosNoCompletado: [...motivos, { id: nuevoId('nc'), nombre: '', activo: true }],
              })
            }
          >
            <Plus className="mr-1 h-4 w-4" />
            Agregar motivo
          </Button>
        </CardBody>
      </Card>

      {/* ------------------- SLA Y REINCIDENCIA (S-09) ------------------- */}
      <Card id="trabajos-urgencia">
        <CardHeader>
          <CardTitle>Urgencia de las garantías</CardTitle>
          <CardDescription>
            En cuánto tiempo hay que atender una garantía, y cuándo avisar de un cliente que ya
            reclamó antes.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <label className="block max-w-sm">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Atender una garantía antes de (horas)
            </span>
            <Input
              type="number"
              min={1}
              max={720}
              value={String(value.garantiaSlaHoras ?? 24)}
              onChange={(e) => onChange({ garantiaSlaHoras: Number(e.target.value) })}
            />
            <span className="mt-1 block text-xs text-slate-500">
              Pasado ese tiempo la garantía aparece marcada como vencida en el tablero. Si el
              cliente quedó afuera de su casa, esa garantía va primero igual, sin importar este
              número.
            </span>
          </label>

          {/* La ventana de reincidencia sale de la COBERTURA DEL MODELO. Este
              campo quedó como salida de emergencia, no como el modo normal. */}
          <label className="block max-w-sm">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Forzar una ventana fija para los avisos de repetidos (días)
            </span>
            <Input
              type="number"
              min={1}
              max={3650}
              placeholder="Usar la cobertura de cada modelo"
              value={
                value.reincidenciaVentanaDias == null ? '' : String(value.reincidenciaVentanaDias)
              }
              onChange={(e) => {
                const v = e.target.value.trim();
                onChange({ reincidenciaVentanaDias: v === '' ? null : Number(v) });
              }}
            />
            <span className="mt-1 block text-xs text-slate-500">
              Déjelo vacío: así cada equipo se mide con su propio período de garantía, el que usted
              cargó arriba en Modelos. Un P20 de 12 meses se mira 12 meses; uno de 24, 24.
            </span>
            {value.reincidenciaVentanaDias == null ? (
              <span className="mt-1 flex items-start gap-2 rounded-md bg-slate-50 p-2 text-xs text-slate-600">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Le van a llegar <strong>dos avisos distintos</strong>, y cada uno se prende y se
                  apaga por separado en Mensajes → ruteo:
                  <br />«<strong>La misma artículo volvió a fallar</strong>», que es la que
                  alimenta el reporte de garantías, y «
                  <strong>Cliente con garantías repetidas</strong>», que solo suena si al cliente
                  todavía le queda algún equipo en cobertura.
                  <br />
                  El plazo se cuenta desde la falla anterior, no desde la instalación: si una
                  artículo falla, se repara y vuelve a fallar al mes, cuenta como repetida aunque
                  la garantía original ya se haya vencido.
                </span>
              </span>
            ) : (
              <span className="mt-1 flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Con un número acá todos los modelos se miden igual y se ignora la cobertura del
                catálogo. Borre el campo para volver al modo normal.
              </span>
            )}
          </label>
        </CardBody>
      </Card>

      {/* ------------------- UBICACIÓN Y GEOCERCA (S-05) ----------------- */}
      <Card id="trabajos-ubicacion">
        <CardHeader>
          <CardTitle>Ubicación de los trabajos</CardTitle>
          <CardDescription>
            A qué distancia del destino se considera que el técnico cerró donde debía, y si el
            teléfono tiene que dar la ubicación para poder cerrar.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <label className="block max-w-sm">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Distancia aceptable del destino (metros)
            </span>
            <Input
              type="number"
              min={1}
              max={100000}
              value={String(value.geocerca?.radioMetrosDefault ?? 300)}
              onChange={(e) =>
                onChange({
                  geocerca: {
                    ...value.geocerca,
                    radioMetrosDefault: Number(e.target.value),
                  },
                })
              }
            />
            <span className="mt-1 block text-xs text-slate-500">
              Si el técnico cierra más lejos que esto, el trabajo queda marcado para que usted lo
              revise. Se aplica a todas las zonas.
            </span>
          </label>

          <div className="rounded-md border border-slate-200 p-3">
            <div className="flex items-center gap-2">
              <Toggle
                checked={value.geocerca?.exigirGpsAlCerrar ?? false}
                label="Exigir la ubicación del teléfono para cerrar"
                onChange={(exigirGpsAlCerrar) =>
                  onChange({ geocerca: { ...value.geocerca, exigirGpsAlCerrar } })
                }
              />
              <span className="text-sm text-slate-700">
                No dejar cerrar si el teléfono no da la ubicación
              </span>
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Encendido: el técnico no puede cerrar sin ubicación. Apagado: puede cerrar igual y el
              trabajo queda marcado como «cerrado sin ubicación» para que usted lo vea.
            </p>
            {value.geocerca?.exigirGpsAlCerrar ? (
              <p className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                El mes pasado 50 de 309 cierres no tuvieron ubicación. Con esto encendido, esos 50
                técnicos habrían quedado trabados en la calle. Si empieza a pasar, apáguelo.
              </p>
            ) : (
              <p className="mt-2 flex items-start gap-2 rounded-md bg-slate-50 p-2 text-xs text-slate-600">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Arranca apagado a propósito: primero conviene ver cuántos cierres llegan sin
                ubicación sin trabar a nadie. Los que lleguen así quedan marcados en el listado.
                Cuando la mayoría de los trabajos tenga su punto de destino cargado, enciéndalo.
              </p>
            )}
          </div>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Clave del servicio de mapas
            </span>
            <Input
              type="password"
              autoComplete="off"
              placeholder="Sin clave: las direcciones se ubican a mano"
              value={value.geocerca?.mapsApiKey ?? ''}
              onChange={(e) =>
                onChange({
                  geocerca: { ...value.geocerca, mapsApiKey: e.target.value || null },
                })
              }
            />
            <span className="mt-1 block text-xs text-slate-500">
              Con la clave puesta, el sistema ubica solo la dirección en el mapa al agendar. Sin
              clave, o si el servicio falla, el punto se carga a mano como hasta ahora — nunca
              impide agendar un trabajo.
            </span>
          </label>

          {/* PUNTO 5 · El dueño pidió saber cuánto gastaría antes de decidir.
              No podemos cotizarle el servicio (el precio lo pone el proveedor),
              así que le damos el número de consultas y decide con su tarifa. */}
          {consumo.data && (
            <div className="rounded-md bg-slate-50 p-3 text-sm dark:bg-slate-800">
              {consumo.data.tieneClave ? (
                <p className="text-slate-700 dark:text-slate-200">
                  Este mes se hicieron <strong>{consumo.data.consultasReales}</strong> consultas al
                  servicio de mapas.
                </p>
              ) : (
                <>
                  <p className="text-slate-700 dark:text-slate-200">
                    Este mes se habrían necesitado{' '}
                    <strong>{consumo.data.consultasEstimadas}</strong> consultas al servicio de
                    mapas.
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Es la cuenta de los trabajos con dirección escrita a los que hubo que ponerles
                    el punto a mano. Multiplíquelo por lo que cobre su proveedor para saber cuánto
                    le costaría al mes. Sin clave no se gasta nada.
                  </p>
                </>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* ------------------- RESUMEN DIARIO (S-14) ----------------------- */}
      <Card id="trabajos-resumen">
        <CardHeader>
          <CardTitle>Resumen del día</CardTitle>
          <CardDescription>
            Un solo mensaje al final del día con todo lo que pasó, en vez de un aviso por cada
            llegada y cada cierre. Se siguen avisando al instante, y solo ellos: un cierre fuera de
            la zona del cliente y un trabajo vencido sin que el técnico haya salido.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex items-center gap-2">
            <Toggle
              checked={value.resumenDiario?.activo ?? true}
              label="Mandar el resumen del día"
              onChange={(activo) => onChange({ resumenDiario: { ...value.resumenDiario, activo } })}
            />
            <span className="text-sm text-slate-700">Mandar el resumen del día</span>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">¿A qué hora?</span>
              <Input
                type="time"
                className="w-36"
                value={value.resumenDiario?.hora ?? '20:00'}
                onChange={(e) =>
                  onChange({ resumenDiario: { ...value.resumenDiario, hora: e.target.value } })
                }
              />
            </label>

            <Select
              label="¿Por dónde?"
              className="w-44"
              value={value.resumenDiario?.canal ?? 'whatsapp'}
              options={[
                { value: 'whatsapp', label: 'WhatsApp' },
                { value: 'email', label: 'Correo' },
              ]}
              onChange={(e) =>
                onChange({
                  resumenDiario: {
                    ...value.resumenDiario,
                    canal: e.target.value as 'whatsapp' | 'email',
                  },
                })
              }
            />
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-700">¿Qué incluye?</legend>
            {(
              [
                ['completados', 'Trabajos completados'],
                ['noCompletados', 'No completados, con el motivo de cada uno'],
                ['vencidos', 'Vencidos sin atender'],
                ['cierresFueraDeRadio', 'Cierres fuera de la zona del cliente'],
                ['garantiasAbiertas', 'Garantías abiertas'],
                ['reincidencias', 'Clientes con garantías repetidas'],
                ['montoVendido', 'Monto vendido'],
                [
                  'esperandoPersona',
                  'Conversaciones esperando a una persona (quejas, pases a humano)',
                ],
              ] as [keyof NonNullable<typeof value.resumenDiario>['incluye'], string][]
            ).map(([clave, etiqueta]) => (
              <label
                key={clave}
                className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  checked={value.resumenDiario?.incluye?.[clave] ?? true}
                  onChange={(e) =>
                    onChange({
                      resumenDiario: {
                        ...value.resumenDiario,
                        incluye: {
                          ...value.resumenDiario.incluye,
                          [clave]: e.target.checked,
                        },
                      },
                    })
                  }
                />
                {etiqueta}
              </label>
            ))}
          </fieldset>

          <p className="flex items-start gap-2 rounded-md bg-slate-50 p-3 text-xs text-slate-600">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Los avisos de «el técnico llegó» y «el técnico terminó» quedaron apagados porque ahora
            viajan acá dentro. Si los quiere de vuelta uno por uno, se encienden en Mensajes.
          </p>
        </CardBody>
      </Card>

      {/* --------------- ENCUESTA DE GARANTÍAS (punto 6) ----------------- */}
      <Card id="trabajos-encuesta">
        <CardHeader>
          <CardTitle>Encuesta de garantías</CardTitle>
          <CardDescription>
            Las preguntas que se le harían al cliente después de atenderle una garantía. Están
            cargadas y guardadas, pero <strong>apagadas</strong>: no se envía nada hasta que usted
            las encienda.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex items-center gap-2">
            <Toggle
              checked={value.encuestaGarantia?.activa ?? false}
              label="Enviar la encuesta de garantías"
              onChange={(activa) =>
                onChange({ encuestaGarantia: { ...value.encuestaGarantia, activa } })
              }
            />
            <span className="text-sm text-slate-700">Enviar la encuesta de garantías</span>
          </div>

          {!(value.encuestaGarantia?.activa ?? false) && (
            <p className="flex items-start gap-2 rounded-md bg-slate-50 p-3 text-xs text-slate-600">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Apagada por decisión suya: primero quería ver cuánto bajó el volumen de mensajes con
              el resumen del día. El contador de abajo se lo dice.
            </p>
          )}

          <ol className="space-y-2">
            {(value.encuestaGarantia?.preguntas ?? []).map((q, i) => (
              <li key={q.id} className="rounded-md border border-slate-200 p-3">
                <Input
                  value={q.texto}
                  onChange={(e) => {
                    const next = [...(value.encuestaGarantia?.preguntas ?? [])];
                    next[i] = { ...q, texto: e.target.value };
                    onChange({ encuestaGarantia: { ...value.encuestaGarantia, preguntas: next } });
                  }}
                />
                <span className="mt-1 block text-xs text-slate-500">
                  {q.tipo === 'escala'
                    ? 'Responde con un número del 1 al 5'
                    : q.tipo === 'opciones'
                      ? `Elige una opción: ${q.opciones.join(' · ')}`
                      : 'Responde escribiendo lo que quiera'}
                </span>
              </li>
            ))}
          </ol>

          {saliente.data && (
            <div className="rounded-md bg-slate-50 p-3 text-sm dark:bg-slate-800">
              <p className="text-slate-700 dark:text-slate-200">
                Este mes salieron <strong>{saliente.data.mensajes}</strong> mensajes automáticos.
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Cuenta lo que manda el asistente y los avisos del sistema; no lo que escribe una
                persona desde el panel. Compárelo con el mes pasado para ver cuánto bajó con el
                resumen del día, y decida con eso si enciende la encuesta.
              </p>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={porBorrar !== null}
        onClose={() => setPorBorrar(null)}
        title="¿Quitar de la lista?"
      >
        <p className="text-sm text-slate-600">
          Se va a quitar <strong>{porBorrar?.nombre || 'este elemento'}</strong> de la lista. Los
          trabajos que ya lo usaron conservan lo que se les cargó; solo deja de aparecer para los
          nuevos.
        </p>
        <p className="mt-2 text-sm text-slate-600">
          El cambio se aplica cuando apriete <strong>Guardar Trabajos</strong>.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setPorBorrar(null)}>
            Cancelar
          </Button>
          <Button type="button" variant="danger" onClick={confirmarBorrado}>
            Quitar
          </Button>
        </div>
      </Modal>
    </div>
  );
}
